<script setup lang="ts">
import { AdyenCheckout } from '@adyen/adyen-web'
import { BrinkPaymentErrorCodes } from 'types/vendors/brink'

import { getAdyenPaymentAmount } from '~/lib/vendors/adyen/getAdyenPaymentAmount'
import '@adyen/adyen-web/styles/adyen.css'

const emit = defineEmits(['paymentError'])

function isAdyenCheckout(checkout: any): checkout is typeof AdyenCheckout {
  return checkout && typeof checkout !== 'undefined'
}
const checkoutStore = useCheckoutStore()
const orderStore = useOrderStore()
const cartStore = useCartStore()
const storefrontStore = useStorefrontStore()
const router = useRouter()
const route = useRoute()
const { currency } = storeToRefs(storefrontStore)

const { $checkoutUrl } = useNuxtApp()
const orderSignature = ref()

const applepaycontainer = ref<HTMLDivElement>()
const expressErrorArray = ref<string[]>([''])

const { currentMarketCountryCode } = storeToRefs(storefrontStore)

// watch market change and rerun the flow
watch(currentMarketCountryCode, async (oldValue: string, newValue: string) => {
  if (oldValue === newValue)
    return
  checkoutStore.loading = true
  expressErrorArray.value = ['']
  await startExpressFlow()
  checkoutStore.loading = false
})

onMounted(async () => {
  checkoutStore.loading = true

  try {
    await startExpressFlow()
    checkoutStore.loading = false
  }
  catch (error: any) {
    checkoutStore.loading = false
    if (error.code === BrinkPaymentErrorCodes.MISSING_SHIPPING_OPTION_ERROR)
      return router.push($checkoutUrl({ route, view: 'address' }))

    if (error.code === BrinkPaymentErrorCodes.UNAUTHORIZED)
      return router.push($checkoutUrl({ route, view: 'cart' }))

    if (error.code === BrinkPaymentErrorCodes.TAX_JAR_ERROR)
      return router.push($checkoutUrl({ route, view: 'address' }))

    emit('paymentError', error)
  }
})

async function startExpressFlow() {
  const amount = getAdyenPaymentAmount({ priceTotal: cartStore.cartSummary.priceTotal * 100, currency: currency.value.code })

  const checkoutApplePay = await orderStore.createAdvancedFlowApplePay(expressErrorArray, amount, emit, orderSignature)
  if (!isAdyenCheckout(checkoutApplePay))
    throw new TypeError('Invalid adyen checkout')

  const applePay = await orderStore.setUpApplePayExpress(checkoutApplePay, expressErrorArray)

  if (!applePay)
    throw new ReferenceError('Apple Pay Payment container element does not exist')

  applePay
    .isAvailable()
    .then(() => {
      if (!applepaycontainer.value)
        throw new ReferenceError('Apple Pay Payment container element does not exist')

      applePay.mount(applepaycontainer.value)
      const child = applepaycontainer.value.firstElementChild as HTMLElement

      if (child) {
        child.style.setProperty('width', '100%', 'important')
      }
    })
    .catch((e) => {
      console.log('apple pay not available')
    })

  if (route.query.redirectResult) {
    const { signature } = route.query
    orderSignature.value = signature
  }
  else {
    checkoutStore.loading = false
  }
}
</script>

<template>
  <div id="component-container">
    <div
      id="applepay-container"
      ref="applepaycontainer"
    />
    <p
      v-if="expressErrorArray"
      class="red"
      v-html="expressErrorArray.join('<br/>')"
    />
  </div>
</template>

<style lang="scss" scoped>
.red {
  color: var(--red);
}
#component-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-block: 2.5rem;
  margin-inline: 1.6rem;
}
#applepay-container {
  width: 100%;
}
@media screen and (min-width:768px) {
  #component-container {
  margin-inline: 0rem;
  }
}
</style>
